import React, { useEffect, useRef, useState } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { loginRequest } from '../authConfig';
import { getTokenManager } from '../services/api';

const ProtectedRoute = ({ children }) => {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [isLoading, setIsLoading] = useState(true);
  const didTriggerLogin = useRef(false);

  useEffect(() => {
    const checkAuth = async () => {
      // Wait until MSAL is idle
      if (inProgress !== 'none') {
        return;
      }

      if (!isAuthenticated) {
        if (!didTriggerLogin.current) {
          didTriggerLogin.current = true;
          console.log('🔐 User not authenticated, redirecting to login');
          instance.loginRedirect(loginRequest);
        }
        return;
      }

      // If we get here, user is authenticated
      console.log('✅ User authenticated, setting up session');

      // Ensure we have an active account
      const accounts = instance.getAllAccounts();
      if (accounts.length > 0 && !instance.getActiveAccount()) {
        instance.setActiveAccount(accounts[0]);
        console.log('👤 Active account set:', accounts[0].username);
      }

      // Token management is now handled by TokenManager service
      // Just ensure the token manager is aware of the current session
      const tokenManager = getTokenManager();
      if (tokenManager) {
        console.log('🔄 TokenManager found, ensuring valid token');
        tokenManager.ensureValidToken().then(() => {
          console.log('✅ Token validated');
          setIsLoading(false);
        }).catch((error) => {
          console.error('❌ Token validation failed:', error);
          setIsLoading(false); // Continue anyway, let API handle token refresh
        });
      } else {
        console.warn('⚠️ TokenManager not available, using fallback');
        setIsLoading(false);
      }
    };

    if (inProgress === 'none') {
      checkAuth();
    }
  }, [instance, isAuthenticated, inProgress]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body1" color="text.secondary">
          Setting up your session...
        </Typography>
      </Box>
    );
  }

  return isAuthenticated ? children : null;
};

export default ProtectedRoute;
